import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import DatePicker, { DatePickerChangeEvent } from 'components/DatePicker';
import moment from 'moment';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import { useHistory } from 'react-router-dom';
import { postSolicitudSuspension, getSuspensionEligible } from '../../features/matriculas/matriculasAPI';
import { SuspensionMatricula } from 'types/Matriculas';
import UploaderMultiple from 'components/UploaderMultiple';
import { useTotp } from 'hooks/useTotp';

export default function SolicitudSuspension() {
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof SuspensionMatricula, boolean>>
  >({});

  const [isLoading, setLoading] = useState(false);
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [eligibilityReasons, setEligibilityReasons] = useState<string[]>([]);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const { requestTotp, TotpModal } = useTotp();

  useEffect(() => {
    setLoading(true);
    getSuspensionEligible()
      .then(result => {
        setEligible(result.eligible);
        setEligibilityReasons(result.reasons ?? []);
      })
      .catch(err => {
        console.error('Error verificando elegibilidad de suspensión', err);
        setEligible(null);
        setEligibilityReasons([
          'No se pudo verificar la elegibilidad para solicitar la suspensión. Reintente más tarde.',
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const [data, setData] = useState<SuspensionMatricula>({
    id: '',
    state_id: 0,
    matriculate_id: 0,
    motivo_solicitud: '',
    fecha_desde: '',
    fecha_hasta: '',
    motivo_rechazo: '',
    created_at: '',
    resolved_at: '',
    deleted_at: '',
    user_id: 0,
  });

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de suspensión?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const data: Partial<SuspensionMatricula> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      data[key as keyof SuspensionMatricula] = value.toString() as any;
    });

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    // Re-verificamos elegibilidad antes de enviar la solicitud.
    try {
      const eligibility = await getSuspensionEligible();
      if (!eligibility.eligible) {
        const detail = eligibility.reasons.length > 0
          ? eligibility.reasons.join('\n\n')
          : 'No es posible solicitar la suspensión en este momento.';
        alert(detail);
        setEligible(false);
        setEligibilityReasons(eligibility.reasons ?? []);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Error verificando elegibilidad de suspensión', err);
      alert('No se pudo verificar la elegibilidad. Por favor intente nuevamente.');
      setLoading(false);
      return;
    }

    const code = await requestTotp('matricula_suspension');
    if (!code) return;

    // Enviamos la petición
    await postSolicitudSuspension(data, files, code)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('SuspensionCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ?? `Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.`,
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/dashboard');
        });
      })
      .catch(err => {
        console.error(
          'SuspensionCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | Date | null;
    value = target.value;
    if (typeof value === 'string' && ['dni', 'cuit', 'telefono', 'legal_telefono', 'legal_celular'].includes(name)) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">
                    Solicitud de Suspensión de Matrícula
                  </CardTitle>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              { eligible === false && (
                <div className="alert alert-warning" role="alert">
                  <strong>No es posible solicitar la suspensión de matrícula:</strong>
                  { eligibilityReasons.length > 0
                    ? <ul className="mb-0 mt-2">{ eligibilityReasons.map((r, i) => <li key={i}>{ r }</li>) }</ul>
                    : <p className="mb-0 mt-2">No es posible solicitar la suspensión en este momento.</p>
                  }
                </div>
              )}
              <Form innerRef={ formElement }>
                <Col sm={12}>
                  <CardTitle>
                    Datos
                  </CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="fecha_desde">
                        Fecha desde <Required />
                      </Label>
                      <DatePicker
                        id="fecha_desde"
                        name="fecha_desde"
                        selected={data.fecha_desde ?? moment().toDate()}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha desde"
                        className="date-picker form-control"
                        minDate={moment().toDate()}
                        disabled={isLoading}
                        onChange={date => {
                          handleInputChange({
                            target: {
                              name: 'fecha_desde',
                              type: 'datepicker',
                              value: date,
                            },
                          });
                        }}
                        autoComplete="off"
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="fecha_hasta">
                        Fecha hasta <Required />
                      </Label>
                      <DatePicker
                        id="fecha_hasta"
                        name="fecha_hasta"
                        selected={data.fecha_hasta ?? moment().toDate()}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha hasta"
                        className="date-picker form-control"
                        minDate={
                          data.fecha_desde
                            ? moment(data.fecha_desde).toDate()
                            : moment().toDate()
                        }
                        maxDate={
                          data.fecha_desde
                            ? moment(data.fecha_desde).add(2, 'years').toDate()
                            : moment().add(2, 'years').toDate()
                        }
                        disabled={isLoading}
                        onChange={date => {
                          handleInputChange({
                            target: {
                              name: 'fecha_hasta',
                              type: 'datepicker',
                              value: date,
                            },
                          });
                        }}
                        autoComplete="off"
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="documentacion">
                        Motivo de la suspensión <Required />
                      </Label>
                      <Input
                        id="motivo_solicitud"
                        name="motivo_solicitud"
                        type="textarea"
                        invalid={formValidation.motivo_solicitud === false}
                        value={data.motivo_solicitud ?? ''}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      />
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>
                    Adjuntos
                  </CardTitle>
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="documentacion_motivo">
                        Documentación respaldatoria del motivo <Required />
                      </Label>
                      <UploaderMultiple
                        name='documentacion_motivo'
                        required
                        accept={['application/pdf']}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <p>
                  Requisitos para su solicitud:
                </p>
                <p>
                  a) Estar al día con todas las obligaciones colegiales (matricula, intereses, MOV, entre otras). Si
                  abona la matricula en cuotas deberá pagar el sado del año, por ser la matrícula una obligación anual.
                </p>
                <p>
                  b) El período de suspensión deberá ser mayor a 6 meses y menor a 2 años.
                </p>
                <p>
                  c) Entregar la credencial (física) en caso de poseerla, o denuncia de pérdida o robo ante autoridad
                  policial. En caso de poseer matricula digital no es necesario este requisito.
                </p>
                <p>
                  d) Constancias y documentación que fundamente el pedido.
                </p>
                <p>
                  Nota: El Consejo Ejecutivo se halla facultado: 1) Para rechazar in límine los fundamentos expuestos y
                  la prueba de que intenta valerse el peticionante y, por ende, la petición. 2) Para requerir del
                  solicitante, acompañe otros elementos objetivos a los mismos fines. 3) Para limitar el lapso de
                  suspensión solicitado a los parámetros y requisitos del presente reglamento.
                </p>
                <p>
                  Resolución Consejo Directivo 4/99
                </p>
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleSendForm}
                      disabled={isLoading || eligible !== true}
                    >
                      Solicitar
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
              {TotpModal}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
